import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Menu, Modal, Button, Typography, Space, Avatar, Input } from 'antd';
import { LogoutOutlined, SearchOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { UtilisateurService } from '../Services/UtilisateurService';
import { findMenuItemByKey, findMenuItemByPath, menuConfig } from '../Services/MenuConfig';

const rootSubmenuKeys = ['dashboard', 'structure', 'params', 'docs', 'extradocs', 'process', 'compta', 'extra', 'paie', 'text&reglement', 'signout'];

/**
 * Utilitaire pour transformer la configuration du menu centralisée en items Ant Design
 * Ajoute les propriétés nécessaires pour Ant Design
 */
const transformMenuConfigToAntItems = (config) => {
    return config.map(item => ({
        ...item,
        children: item.children ? item.children.map(child => ({
            ...child,
            disabled: child.disabled !== undefined ? child.disabled : false
        })) : undefined
    }));
};

/**
 * Applique les privilèges de l'utilisateur aux items du menu
 */
function applyPrivilegesToMenuItems(items, privileges) {
    // Crée une map pour un accès rapide aux privilèges par alias
    const privilegeMap = {};
    privileges.forEach(priv => {
        privilegeMap[priv.alias] = priv.valeur;
    });

    // Parcours récursif des items pour appliquer la propriété disabled
    return items.map(item => {
        if (item.children && Array.isArray(item.children)) {
            return {
                ...item,
                children: item.children.map(child => ({
                    ...child,
                    disabled: privilegeMap.hasOwnProperty(child.key)
                        ? !privilegeMap[child.key] // disabled = true si valeur = false
                        : child.disabled // garde la valeur existante si pas de correspondance
                }))
            };
        }
        return item;
    });
}

export default function SideMenu({ onLogout }) {
    const navigate = useNavigate();
    const location = useLocation();
    const userProfil = localStorage.getItem("profil");
    const roleName = localStorage.getItem("role_name");
    const [openKeys, setOpenKeys] = useState([roleName === 'Point Focal' ? 'docs' : 'dashboard']);
    const [selectedKeys, setSelectedKeys] = useState([window.location.pathname]);
    const gestionUsersService = new UtilisateurService();

    const [privileges, setPrivileges] = useState([]);

    const getRouteBasePath = (pathname) => {
        const pathParts = pathname.split('/').filter(Boolean);

        if (pathParts.length >= 2 && (pathParts[1] === 'edit' || pathParts[1] === 'view' || pathParts[1] === 'show')) {
            return `/${pathParts[0]}`;
        }

        return pathname;
    };

    const getMenuStateFromPath = (pathname) => {
        const basePath = getRouteBasePath(pathname);
        const { parent, child } = findMenuItemByPath(basePath, menuConfig);

        if (child) {
            return {
                selectedKey: child.key,
                openKey: parent ? parent.key : null,
            };
        }

        // Cas spécifique dashboard: route "/dashboard" <-> key "dashboard"
        if (pathname === '/dashboard' || pathname === '/') {
            return {
                selectedKey: 'dashboard',
                openKey: null,
            };
        }

        return {
            selectedKey: null,
            openKey: null,
        };
    };

    const getNavigationPathFromKey = (key) => {
        const { child } = findMenuItemByKey(key, menuConfig);

        if (child?.path) {
            return child.path;
        }

        // Fallback pour conserver le comportement actuel si la clé n'est pas dans la config
        return key;
    };

    const onOpenChange = (keys) => {
        const latestOpenKey = keys.find((key) => openKeys.indexOf(key) === -1);
        if (latestOpenKey && rootSubmenuKeys.indexOf(latestOpenKey) === -1) {
            setOpenKeys(keys);
        } else {
            setOpenKeys(latestOpenKey ? [latestOpenKey] : []);
        }
    };

    // Écouter l'événement personnalisé pour sélectionner un item du menu
    useEffect(() => {
        const handleSelectMenuItem = (event) => {
            const { key } = event.detail;
            setSelectedKeys([key]);
            setOpenKeys([]); // Fermer tous les sous-menus
        };

        window.addEventListener('selectMenuItem', handleSelectMenuItem);

        return () => {
            window.removeEventListener('selectMenuItem', handleSelectMenuItem);
        };
    }, []);

    // Synchronise automatiquement le menu quand la navigation vient d'un autre composant
    useEffect(() => {
        const { selectedKey, openKey } = getMenuStateFromPath(location.pathname);

        if (selectedKey) {
            setSelectedKeys([selectedKey]);
        }

        if (openKey) {
            setOpenKeys([openKey]);
        } else if (selectedKey) {
            setOpenKeys([]);
        }
    }, [location.pathname]);

    // Récupérer les privilèges de l'utilisateur
    useEffect(() => {
        const fetchPrivileges = async () => {
            if (userProfil) {
                try {
                    const privileges = await gestionUsersService.getUserPrivileges(userProfil);
                    setPrivileges(privileges);
                    console.log('Privilèges récupérés :', privileges);
                } catch (error) {
                    console.error('Erreur lors de la récupération des privilèges :', error);
                }
            }
        };

        fetchPrivileges();
    }, [userProfil]);

    // Transformer la configuration centralisée en items Ant Design
    const baseItems = transformMenuConfigToAntItems(menuConfig);

    // Appliquer les privilèges
    const menuItemsWithPrivileges = applyPrivilegesToMenuItems(baseItems, privileges);

    // Filtrer les menus racines si le role est "Point Focal" pour ne laisser que "docs" et "signout"
    const [logoutModalVisible, setLogoutModalVisible] = useState(false);
    const userName = localStorage.getItem('login') || 'Utilisateur';

    const handleLogout = () => {
        localStorage.clear();
        navigate('/login', { replace: true });
        onLogout();
    };

    const filteredMenuItems = roleName === 'Point Focal'
        ? menuItemsWithPrivileges.filter(
            mi => mi.key === 'docs' || mi.key === 'structure' || mi.key === 'signout'
        )
        : menuItemsWithPrivileges;

    // --- Recherche ---
    const [searchQuery, setSearchQuery] = useState('');
    const searchInputRef = useRef(null);

    const flatMenuItems = useMemo(() => {
        const items = [];
        filteredMenuItems.forEach(parent => {
            if (parent.type === 'divider' || parent.key === 'signout') return;
            if (parent.children) {
                parent.children.forEach(child => {
                    items.push({ ...child, parentLabel: parent.label, parentIcon: parent.icon });
                });
            } else {
                items.push({ ...parent, parentLabel: null });
            }
        });
        return items;
    }, [filteredMenuItems]);

    const searchResults = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return [];
        return flatMenuItems.filter(item =>
            item.label?.toLowerCase().includes(q) ||
            item.parentLabel?.toLowerCase().includes(q)
        );
    }, [searchQuery, flatMenuItems]);

    const highlight = (text, query) => {
        if (!query || !text) return text;
        const idx = text.toLowerCase().indexOf(query.toLowerCase());
        if (idx === -1) return text;
        return (
            <span>
                {text.slice(0, idx)}
                <mark style={{ background: '#faad14', color: '#000', borderRadius: 3, padding: '0 2px', fontWeight: 700 }}>
                    {text.slice(idx, idx + query.length)}
                </mark>
                {text.slice(idx + query.length)}
            </span>
        );
    };

    const handleSearchNavigate = (item) => {
        if (item.disabled) return;
        setSearchQuery('');
        setSelectedKeys([item.key]);
        const { parent } = findMenuItemByKey(item.key, menuConfig);
        if (parent) setOpenKeys([parent.key]);
        navigate(item.path || item.key);
    };

    return (
        <div>
            {/* Barre de recherche */}
            <div style={{ padding: '10px 12px 6px' }}>
                <Input
                    ref={searchInputRef}
                    placeholder="Rechercher un menu..."
                    prefix={<SearchOutlined style={{ color: 'rgba(255,255,255,0.45)' }} />}
                    suffix={
                        searchQuery
                            ? <CloseCircleOutlined
                                style={{ color: 'rgba(255,255,255,0.45)', cursor: 'pointer' }}
                                onClick={() => { setSearchQuery(''); searchInputRef.current?.focus(); }}
                              />
                            : null
                    }
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    allowClear={false}
                    style={{
                        background: 'rgba(255,255,255,0.08)',
                        border: '1px solid rgba(255,255,255,0.15)',
                        borderRadius: 8,
                        color: '#fff',
                        fontSize: 13,
                    }}
                    styles={{ input: { color: '#fff', background: 'transparent' } }}
                />
            </div>

            {/* Résultats de recherche */}
            {searchQuery.trim() && (
                <div style={{
                    margin: '0 8px 6px',
                    background: '#0d1a26',
                    borderRadius: 8,
                    border: '1px solid rgba(255,255,255,0.1)',
                    maxHeight: 320,
                    overflowY: 'auto',
                }}>
                    {searchResults.length === 0 ? (
                        <div style={{ padding: '16px 12px', color: 'rgba(255,255,255,0.4)', fontSize: 13, textAlign: 'center' }}>
                            Aucun résultat pour « {searchQuery} »
                        </div>
                    ) : (
                        searchResults.map(item => (
                            <div
                                key={item.key}
                                onClick={() => handleSearchNavigate(item)}
                                style={{
                                    padding: '9px 14px',
                                    cursor: item.disabled ? 'not-allowed' : 'pointer',
                                    opacity: item.disabled ? 0.4 : 1,
                                    borderBottom: '1px solid rgba(255,255,255,0.06)',
                                    transition: 'background 0.15s',
                                }}
                                onMouseEnter={e => { if (!item.disabled) e.currentTarget.style.background = 'rgba(22,119,255,0.25)'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 14 }}>{item.icon}</span>
                                    <div>
                                        <div style={{ color: '#fff', fontSize: 13, fontWeight: 600, lineHeight: 1.3 }}>
                                            {highlight(item.label, searchQuery)}
                                        </div>
                                        {item.parentLabel && (
                                            <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, marginTop: 1 }}>
                                                {item.parentLabel}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}

            <Menu
                onClick={({ key }) => {
                    if (key === "signout") {
                        setLogoutModalVisible(true);
                    } else {
                        setSelectedKeys([key]);
                        navigate(getNavigationPathFromKey(key));
                    }
                }}
                selectedKeys={selectedKeys}
                mode="inline"
                items={filteredMenuItems}
                theme='dark'
                openKeys={openKeys}
                onOpenChange={onOpenChange}
                style={{ fontWeight: 'bold', textDecorationColor: "#fff", color: "#fff" }}
            />

            <Modal
                open={logoutModalVisible}
                onCancel={() => setLogoutModalVisible(false)}
                footer={null}
                centered
                width={400}
                closable={false}
                styles={{ body: { padding: '32px 24px', textAlign: 'center' } }}
            >
                <Space direction="vertical" size={20} style={{ width: '100%' }}>
                    <Avatar
                        size={64}
                        icon={<LogoutOutlined />}
                        style={{ backgroundColor: '#fff1f0', color: '#ff4d4f', fontSize: 28 }}
                    />
                    <div>
                        <Typography.Title level={4} style={{ marginBottom: 4 }}>
                            Déconnexion
                        </Typography.Title>
                        <Typography.Text type="secondary">
                            Êtes-vous sûr de vouloir vous déconnecter,{' '}
                            <strong>{userName}</strong> ?
                        </Typography.Text>
                    </div>
                    <Space style={{ width: '100%', justifyContent: 'center' }} size={12}>
                        <Button
                            size="large"
                            onClick={() => setLogoutModalVisible(false)}
                            style={{ minWidth: 120, borderRadius: 8 }}
                        >
                            Annuler
                        </Button>
                        <Button
                            size="large"
                            type="primary"
                            danger
                            icon={<LogoutOutlined />}
                            onClick={handleLogout}
                            style={{ minWidth: 120, borderRadius: 8 }}
                        >
                            Se déconnecter
                        </Button>
                    </Space>
                </Space>
            </Modal>
        </div>
    );
}
